import { useState } from "react";
import { X, ShieldAlert } from "lucide-react";

/**
 * PasswordPromptModal — owner step-up confirmation for destructive SMS
 * actions. onConfirm(password) should throw on failure so the error
 * surfaces and the modal stays open.
 */
export default function PasswordPromptModal({
  open, title, description, confirmLabel = "Confirm", onConfirm, onClose,
}) {
  const [pw, setPw] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  if (!open) return null;

  const submit = async () => {
    if (!pw) { setErr("Enter your account password."); return; }
    setErr(""); setBusy(true);
    try {
      await onConfirm(pw);
      setPw("");
    } catch (e) {
      setErr(e?.response?.data?.detail || "Password incorrect. Delete cancelled.");
    } finally { setBusy(false); }
  };

  const close = () => { setPw(""); setErr(""); onClose?.(); };

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/40" data-testid="sms-pw-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-[400px] max-w-[92vw] p-5">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <ShieldAlert size={18} style={{ color: "#C0392B" }} />
            <h3 className="text-[15px] font-bold" style={{ color: "#002244" }}>{title}</h3>
          </div>
          <button onClick={close} data-testid="sms-pw-close" style={{ color: "#7B8FA3" }}><X size={18} /></button>
        </div>
        {description && <p className="text-[12.5px] mb-3" style={{ color: "#7B8FA3" }}>{description}</p>}
        <input
          type="password"
          autoFocus
          value={pw}
          onChange={(e) => setPw(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") submit(); }}
          placeholder="Your account password"
          data-testid="sms-pw-input"
          className="w-full border rounded-lg px-3 py-2 text-[14px] focus:outline-none"
          style={{ borderColor: "rgba(3,40,102,0.2)" }}
        />
        {err && <div className="text-[12px] mt-1.5" style={{ color: "#C0392B" }} data-testid="sms-pw-error">{err}</div>}
        <div className="flex justify-end gap-2 mt-4">
          <button onClick={close} className="px-3 py-2 text-[13px] font-semibold" style={{ color: "#7B8FA3" }} data-testid="sms-pw-cancel">Cancel</button>
          <button onClick={submit} disabled={busy} className="px-4 py-2 rounded-lg text-[13px] font-bold text-white disabled:opacity-50" style={{ background: "#C0392B" }} data-testid="sms-pw-confirm">
            {busy ? "Working…" : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
